import React, { useEffect, useRef } from 'react';
import Popover from '@mui/material/Popover';
import { Button, Box, Typography, IconButton } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'common/hook';
import FormProvider from 'common/components/form-provider';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { headerSearchInputSchema } from 'common/schema';
import {
    setCampaignsSearchForm,
    clearCampaignsSearchFormFilter,
} from 'slices/campaignsSlice';
import { BaseMultiCheckbox } from 'common/components/base-checkbox-group';
import BaseDatePicker from 'common/components/base-date-picker';
import { useTranslation } from 'react-i18next';
import CampaignConstants from 'pages/admin/campaign/constants';
import { createStyleSheet } from 'utils/helpers';
import SyncIcon from '@mui/icons-material/Sync';
import { RootState } from 'plugins/store';

interface MainHeaderCampaignFilterPopoverProps {
    open: boolean;
    headerFilterId: string | undefined;
    anchorEl: HTMLButtonElement | null;
    handleClose: () => void;
}

const MainHeaderCampaignFilterPopover: React.FC<
    MainHeaderCampaignFilterPopoverProps
> = ({ open, headerFilterId, anchorEl, handleClose }) => {
    const dispatch = useAppDispatch();
    const campaignsSearchForm = useAppSelector(
        (state: RootState) => state.campaignState.campaignsSearchForm
    );
    const { t } = useTranslation();
    const methods = useForm({
        resolver: yupResolver(headerSearchInputSchema),
        defaultValues: {
            status: campaignsSearchForm.status || [
                CampaignConstants.status.ALL,
            ],
            campaignType: campaignsSearchForm.campaignType || [
                CampaignConstants.service.ALL,
            ],
            startDate: campaignsSearchForm.startDate || '',
            endDate: campaignsSearchForm.endDate || '',
        },
    });

    const { handleSubmit, reset, watch, setValue } = methods;
    const onSubmit = handleSubmit(async (data) => {
        const params = { ...data };
        dispatch(setCampaignsSearchForm(params));
        handleClose();

        return;
    });

    const startDate = watch('startDate');
    const endDate = watch('endDate');
    useEffect(() => {
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            if (start > end) {
                setValue('endDate', '');
            }
        }
    }, [startDate, endDate, setValue]);
    const handleRenewForm = () => {
        dispatch(clearCampaignsSearchFormFilter());
        reset({
            status: [CampaignConstants.status.ALL],
            campaignType: [CampaignConstants.service.ALL],
            startDate: '',
            endDate: '',
        });
    };

    const prevStatusRef = useRef<string[]>([]);

    useEffect(() => {
        const currentStatus = watch('status') || [];
        const previousStatus = prevStatusRef.current;

        const hasAll = currentStatus.includes(CampaignConstants.status.ALL);
        const hadAll = previousStatus.includes(CampaignConstants.status.ALL);

        const allIndividualStatus = Object.values(
            CampaignConstants.status
        ).filter((status) => status !== CampaignConstants.status.ALL);
        const selectedWithoutAll = currentStatus.filter(
            (status) => status !== CampaignConstants.status.ALL
        );

        if (!hadAll && hasAll) {
            setValue('status', [CampaignConstants.status.ALL]);
            prevStatusRef.current = [CampaignConstants.status.ALL];
            return;
        }

        if (hadAll && selectedWithoutAll.length > 0) {
            setValue('status', selectedWithoutAll);
            prevStatusRef.current = selectedWithoutAll;
            return;
        }

        if (
            !hasAll &&
            selectedWithoutAll.length === allIndividualStatus.length
        ) {
            setValue('status', [CampaignConstants.status.ALL]);
            prevStatusRef.current = [CampaignConstants.status.ALL];
            return;
        }

        prevStatusRef.current = currentStatus;
        // eslint-disable-next-line
    }, [watch('status')]);

    const prevCampaignTypeRef = useRef<string[]>([]);

    useEffect(() => {
        const currentCampaignType = watch('campaignType') || [];
        const previousCampaignType = prevCampaignTypeRef.current;

        const hasAll = currentCampaignType.includes(
            CampaignConstants.service.ALL
        );
        const hadAll = previousCampaignType.includes(
            CampaignConstants.service.ALL
        );

        const allIndividualTypes = Object.values(
            CampaignConstants.service
        ).filter((type) => type !== CampaignConstants.service.ALL);

        const selectedWithoutAll = currentCampaignType.filter(
            (type) => type !== CampaignConstants.service.ALL
        );

        if (!hadAll && hasAll) {
            setValue('campaignType', [CampaignConstants.service.ALL]);
            prevCampaignTypeRef.current = [CampaignConstants.service.ALL];
            return;
        }

        if (hadAll && selectedWithoutAll.length > 0) {
            setValue('campaignType', selectedWithoutAll);
            prevCampaignTypeRef.current = selectedWithoutAll;
            return;
        }

        if (
            !hasAll &&
            selectedWithoutAll.length === allIndividualTypes.length
        ) {
            setValue('campaignType', [CampaignConstants.service.ALL]);
            prevCampaignTypeRef.current = [CampaignConstants.service.ALL];
            return;
        }

        prevCampaignTypeRef.current = currentCampaignType;
        // eslint-disable-next-line
    }, [watch('campaignType')]);

    const topPixel = 104;

    const datePickerStyle = {
        '& .MuiInputBase-root': {
            // width: '156px',
            height: '29px',
            borderRadius: '10px',
        },
        '& .MuiIconButton-root svg': {
            width: '14px',
            height: '14px',
        },
    };

    const checkBoxStyle = {
        '& .MuiFormControlLabel-label': {
            fontSize: '20px',
        },
    };

    const groupTitleStyle = {
        fontWeight: 600,
        fontSize: '24px',
        lineHeight: '29px',
        marginBottom: '15px',
    };

    return (
        <Popover
            id={headerFilterId}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            sx={{
                '& .MuiPaper-root.MuiPopover-paper': {
                    top: `${topPixel}px !important`,
                    maxHeight: `min(906px, calc(100% - ${topPixel}px))`,
                    borderRadius: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Box
                    sx={{
                        padding: '20px 30px 85px',
                        width: '430px',
                        overflowY: 'auto',
                        maxHeight: '700px',
                    }}
                >
                    <Box
                        mb={'20px'}
                        display={'flex'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                    >
                        <Typography
                            sx={{
                                color: (theme) => theme.palette.primary.main,
                                fontWeight: 800,
                                fontSize: '27px',
                                lineHeight: '33px',
                            }}
                        >
                            {t('layout.header.filter.title')}
                        </Typography>
                        <IconButton
                            onClick={handleRenewForm}
                            aria-label="refresh"
                        >
                            <SyncIcon
                                sx={{
                                    fontSize: 32,
                                    color: 'var(--color-primary)',
                                }}
                            />
                        </IconButton>
                    </Box>
                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Trạng thái
                            </Typography>
                        </Box>
                        <Box>
                            <BaseMultiCheckbox
                                name="status"
                                options={
                                    Object.keys(CampaignConstants.status).map(
                                        (status: string) => ({
                                            label: t(
                                                `page.campaign.status.${status.toLowerCase()}`
                                            ),
                                            value: CampaignConstants.status[
                                                status as keyof typeof CampaignConstants.status
                                            ],
                                        })
                                    ) as { label: string; value: string }[]
                                }
                                sx={checkBoxStyle}
                            />
                        </Box>
                    </Box>
                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Thời gian
                            </Typography>
                        </Box>
                        <Box
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            gap={1}
                        >
                            {t('layout.header.filter.time.start')}
                            <BaseDatePicker
                                name="startDate"
                                size="small"
                                sx={datePickerStyle}
                                onKeyDown={(e) => {
                                    e.preventDefault();
                                }}
                            />
                            {t('layout.header.filter.time.end')}
                            <BaseDatePicker
                                name="endDate"
                                size="small"
                                sx={datePickerStyle}
                                onKeyDown={(e) => {
                                    e.preventDefault();
                                }}
                            />
                        </Box>
                    </Box>
                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Loại chiến dịch
                            </Typography>
                        </Box>
                        <Box>
                            <BaseMultiCheckbox
                                name="campaignType"
                                options={
                                    Object.keys(CampaignConstants.service).map(
                                        (service: string) => ({
                                            label: t(
                                                `page.campaign.service.${service.toLowerCase()}`
                                            ),
                                            value: CampaignConstants.service[
                                                service as keyof typeof CampaignConstants.service
                                            ],
                                        })
                                    ) as { label: string; value: string }[]
                                }
                                sx={checkBoxStyle}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box
                    display={'flex'}
                    justifyContent={'end'}
                    gap={2}
                    padding={'20px 30px'}
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        backgroundColor: '#fff',
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        style={styles.modalButton}
                    >
                        {t('common.close')}
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        aria-label="filter"
                        onClick={onSubmit}
                        style={styles.modalButton}
                    >
                        {t('layout.header.filter.title')}
                    </Button>
                </Box>
            </FormProvider>
        </Popover>
    );
};

const styles = createStyleSheet({
    modalButton: {
        textTransform: 'none',
        borderRadius: '40px',
        minHeight: '42px',
        minWidth: '84px',
        fontSize: '14px',
        fontWeight: '600',
        borderWidth: '2px',
    },
});

export default MainHeaderCampaignFilterPopover;
